import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash, PencilSimple, FloppyDisk, X, Buildings, Check } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import EmailChipsInput from '@/components/ui/email-chips-input';
import { toast } from 'sonner';
import { useAuth, canManageDepartments } from '@/auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DepartmentsSettings() {
  const { user } = useAuth();
  const canManage = canManageDepartments(user);
  const [departments, setDepartments] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // dept id being edited or 'new'
  const [draft, setDraft] = useState({ name: '', mail_recipients: [], approver_user_ids: [] });

  const load = async () => {
    setLoading(true);
    try {
      const [deptRes, apprRes] = await Promise.all([
        axios.get(`${API}/departments`),
        axios.get(`${API}/departments/approver-candidates`).catch(() => ({ data: [] })),
      ]);
      setDepartments(deptRes.data || []);
      setApprovers(apprRes.data || []);
    } catch (e) {
      toast.error('部署の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing('new');
    setDraft({ name: '', mail_recipients: [], approver_user_ids: [] });
  };

  const startEdit = (dept) => {
    setEditing(dept.id);
    setDraft({
      name: dept.name,
      mail_recipients: dept.mail_recipients || [],
      approver_user_ids: (dept.approvers || []).map((a) => a.user_id).filter(Boolean),
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft({ name: '', mail_recipients: [], approver_user_ids: [] });
  };

  const save = async () => {
    if (!draft.name.trim()) {
      toast.error('部署名を入力してください');
      return;
    }
    const payload = {
      name: draft.name.trim(),
      mail_recipients: draft.mail_recipients,
      approver_user_ids: draft.approver_user_ids || [],
    };
    try {
      if (editing === 'new') {
        await axios.post(`${API}/departments`, payload);
        toast.success('部署を追加しました');
      } else {
        await axios.put(`${API}/departments/${editing}`, payload);
        toast.success('部署を更新しました');
      }
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || '保存に失敗しました');
    }
  };

  const remove = async (dept) => {
    if (!window.confirm(`「${dept.name}」を削除しますか？`)) return;
    try {
      await axios.delete(`${API}/departments/${dept.id}`);
      toast.success('部署を削除しました');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || '削除に失敗しました');
    }
  };

  return (
    <div className="space-y-6" data-testid="departments-settings-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2"
            data-testid="settings-page-title">
            <Buildings size={28} weight="duotone" className="text-teal-700" />
            部署管理
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            部署ごとの通知メール宛先と承認担当者を設定します。フォーム送信時は申請者の所属部署の宛先に通知され、未承認の申請があると承認担当者へリマインドメールが送信されます。
          </p>
        </div>
        {canManage && editing !== 'new' && (
          <Button onClick={startNew} data-testid="btn-add-department" className="bg-teal-700 hover:bg-teal-800">
            <Plus size={18} className="mr-1" /> 部署を追加
          </Button>
        )}
      </div>

      <div className="hidden md:block bg-white rounded-lg border border-zinc-200 overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-zinc-100 border-b-2 border-zinc-300">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-700 w-1/4">部署名</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-700">メール宛先（複数登録可）</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-700 w-48">承認担当者</th>
              {canManage && <th className="text-right px-4 py-3 text-xs font-semibold text-zinc-700 w-40">操作</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {editing === 'new' && (
              <EditRow draft={draft} setDraft={setDraft} approvers={approvers} onSave={save} onCancel={cancelEdit} />
            )}
            {loading ? (
              <tr><td colSpan={canManage ? 4 : 3} className="px-4 py-8 text-center text-zinc-500">読み込み中...</td></tr>
            ) : departments.length === 0 && editing !== 'new' ? (
              <tr><td colSpan={canManage ? 4 : 3} className="px-4 py-8 text-center text-zinc-500">部署が登録されていません</td></tr>
            ) : (
              departments.map((dept) => (
                editing === dept.id ? (
                  <EditRow key={dept.id} draft={draft} setDraft={setDraft} approvers={approvers} onSave={save} onCancel={cancelEdit} />
                ) : (
                  <tr key={dept.id} className="hover:bg-zinc-50 transition-colors" data-testid={`dept-row-${dept.id}`}>
                    <td className="px-4 py-3 text-sm font-medium text-zinc-900 align-top">{dept.name}</td>
                    <td className="px-4 py-3 text-sm text-zinc-600 align-top">
                      {(dept.mail_recipients || []).length === 0
                        ? <span className="text-zinc-400 italic">未設定</span>
                        : (
                          <div className="flex flex-wrap gap-1">
                            {dept.mail_recipients.map((email) => (
                              <span key={email} className="inline-block px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs border border-blue-200">
                                {email}
                              </span>
                            ))}
                          </div>
                        )}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-600 align-top" data-testid={`dept-approver-${dept.id}`}>
                      {(dept.approvers || []).length === 0
                        ? <span className="text-zinc-400 italic">未設定</span>
                        : (
                          <div className="flex flex-wrap gap-1">
                            {dept.approvers.map((a) => (
                              <span key={a.user_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs border border-emerald-200" title={a.email}>
                                {a.name}
                              </span>
                            ))}
                          </div>
                        )}
                    </td>
                    {canManage && (
                      <td className="px-4 py-3 text-right space-x-2 align-top">
                        <Button size="sm" variant="outline" onClick={() => startEdit(dept)} data-testid={`btn-edit-dept-${dept.id}`}>
                          <PencilSimple size={14} className="mr-1" /> 編集
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => remove(dept)} data-testid={`btn-delete-dept-${dept.id}`} aria-label={`${dept.name} を削除`} title={`${dept.name} を削除`} className="text-red-600 hover:bg-red-50">
                          <Trash size={14} />
                        </Button>
                      </td>
                    )}
                  </tr>
                )
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Mobile card list ── */}
      <div className="md:hidden space-y-3" data-testid="departments-mobile-list">
        {editing === 'new' && (
          <MobileDeptEditCard draft={draft} setDraft={setDraft} approvers={approvers} onSave={save} onCancel={cancelEdit} />
        )}
        {loading ? (
          <div className="bg-white rounded-lg border border-zinc-200 px-4 py-8 text-center text-sm text-zinc-500">読み込み中...</div>
        ) : departments.length === 0 && editing !== 'new' ? (
          <div className="bg-white rounded-lg border border-zinc-200 px-4 py-8 text-center text-sm text-zinc-500">部署が登録されていません</div>
        ) : (
          departments.map((dept) => (
            editing === dept.id ? (
              <MobileDeptEditCard key={dept.id} draft={draft} setDraft={setDraft} approvers={approvers} onSave={save} onCancel={cancelEdit} />
            ) : (
              <div key={dept.id} className="bg-white rounded-lg border border-zinc-200 p-4 space-y-2.5" data-testid={`dept-card-${dept.id}`}>
                <p className="text-[15px] font-semibold text-zinc-900">{dept.name}</p>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-1">メール宛先</p>
                  {(dept.mail_recipients || []).length === 0
                    ? <span className="text-sm text-zinc-400 italic">未設定</span>
                    : (
                      <div className="flex flex-wrap gap-1">
                        {dept.mail_recipients.map((email) => (
                          <span key={email} className="inline-block px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs border border-blue-200 break-all">{email}</span>
                        ))}
                      </div>
                    )}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-1">承認担当者</p>
                  {(dept.approvers || []).length === 0
                    ? <span className="text-sm text-zinc-400 italic">未設定</span>
                    : (
                      <div className="flex flex-wrap gap-1">
                        {dept.approvers.map((a) => (
                          <span key={a.user_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs border border-emerald-200">{a.name}</span>
                        ))}
                      </div>
                    )}
                </div>
                {canManage && (
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => startEdit(dept)} data-testid={`btn-edit-dept-${dept.id}-m`}>
                      <PencilSimple size={14} className="mr-1" /> 編集
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => remove(dept)} data-testid={`btn-delete-dept-${dept.id}-m`} aria-label={`${dept.name} を削除`} title={`${dept.name} を削除`} className="text-red-600 hover:bg-red-50">
                      <Trash size={14} />
                    </Button>
                  </div>
                )}
              </div>
            )
          ))
        )}
      </div>
    </div>
  );
}

function MobileDeptEditCard({ draft, setDraft, approvers, onSave, onCancel }) {
  return (
    <div className="bg-white rounded-lg border-2 border-blue-200 p-4 space-y-3" data-testid="dept-mobile-edit-card">
      <div>
        <Label className="text-xs font-semibold text-zinc-600">部署名</Label>
        <Input
          autoFocus
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="例: Engineering"
          className="mt-1"
          data-testid="input-dept-name-m"
        />
      </div>
      <div>
        <Label className="text-xs font-semibold text-zinc-600">メール宛先（複数登録可）</Label>
        <div className="mt-1">
          <EmailChipsInput
            value={draft.mail_recipients}
            onChange={(emails) => setDraft({ ...draft, mail_recipients: emails })}
            placeholder="例: info@sensbey.co.jp"
            testId="input-dept-recipients-m"
          />
        </div>
      </div>
      <div>
        <Label className="text-xs font-semibold text-zinc-600">承認担当者</Label>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {(approvers || []).length === 0 ? (
            <span className="text-xs text-zinc-400 italic">候補者がいません（admin・管理者）</span>
          ) : (
            (approvers || []).map((u) => {
              const ids = draft.approver_user_ids || [];
              const sel = ids.includes(u.id);
              const toggle = () => setDraft({
                ...draft,
                approver_user_ids: sel ? ids.filter((x) => x !== u.id) : [...ids, u.id],
              });
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={toggle}
                  data-testid={`approver-toggle-${u.id}-m`}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    sel ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-zinc-600 border-zinc-300'
                  }`}
                >
                  {sel && <Check size={12} weight="bold" />}
                  {u.name}
                </button>
              );
            })
          )}
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={onSave} className="flex-1 bg-teal-700 hover:bg-teal-800" data-testid="btn-save-dept-m">
          <FloppyDisk size={14} className="mr-1" /> 保存
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} data-testid="btn-cancel-dept-m">
          <X size={14} />
        </Button>
      </div>
    </div>
  );
}

function EditRow({ draft, setDraft, approvers, onSave, onCancel }) {
  return (
    <tr className="bg-blue-50/30 align-top">
      <td className="px-4 py-3 align-top">
        <Input
          autoFocus
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="例: Engineering"
          data-testid="input-dept-name"
        />
      </td>
      <td className="px-4 py-3 align-top">
        <EmailChipsInput
          value={draft.mail_recipients}
          onChange={(emails) => setDraft({ ...draft, mail_recipients: emails })}
          placeholder="例: info@sensbey.co.jp"
          testId="input-dept-recipients"
        />
      </td>
      <td className="px-4 py-3 align-top">
        <div className="flex flex-wrap gap-1.5" data-testid="select-dept-approver">
          {(approvers || []).length === 0 ? (
            <span className="text-xs text-zinc-400 italic">候補者がいません（admin・管理者）</span>
          ) : (
            (approvers || []).map((u) => {
              const ids = draft.approver_user_ids || [];
              const sel = ids.includes(u.id);
              const toggle = () => setDraft({
                ...draft,
                approver_user_ids: sel ? ids.filter((x) => x !== u.id) : [...ids, u.id],
              });
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={toggle}
                  data-testid={`approver-toggle-${u.id}`}
                  title={u.email}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                    sel
                      ? 'bg-emerald-500 text-white border-emerald-500'
                      : 'bg-white text-zinc-600 border-zinc-300 hover:border-emerald-400'
                  }`}
                >
                  {sel && <Check size={12} weight="bold" />}
                  {u.name}
                </button>
              );
            })
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-right space-x-2 align-top">
        <Button size="sm" onClick={onSave} data-testid="btn-save-dept" className="bg-teal-700 hover:bg-teal-800">
          <FloppyDisk size={14} className="mr-1" /> 保存
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} data-testid="btn-cancel-dept">
          <X size={14} />
        </Button>
      </td>
    </tr>
  );
}
